import React from 'react';
import { useRouteMatch } from 'react-router-dom';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import usePage, { PageProvider } from '@dashboard/hooks/usePage';
import { Skeleton } from '@dashboard/components/ui/skeleton';

import InviteStaffWizard from './InviteStaffWizard';

// The wizard seeds its message and ticket choices from the event once, so it waits for it.
const InviteStaffSkeleton: React.FC = () => (
  <div className="mx-auto mt-8 mb-36 w-full max-w-screen-2xl px-4 sm:px-16">
    <div className="absolute top-6 left-4">
      <Skeleton className="size-9" />
    </div>
    <div className="mx-auto mt-20 mb-20 w-full max-w-2xl" aria-busy="true" aria-label="Loading">
      <div className="mb-6 space-y-1 text-center">
        <h1 className="text-xl text-balance">Invite staff</h1>
        <p className="text-sm text-muted-foreground">
          Invite staff to check in guests at your event.
        </p>
      </div>
      <Skeleton className="mx-auto mb-6 h-7 w-[26rem] max-w-full" />
      <div className="space-y-6 rounded-xl border border-border p-6">
        <Skeleton className="h-5 w-16" />
        <div className="flex gap-2">
          <Skeleton className="h-9 flex-1" />
          <Skeleton className="h-9 flex-1" />
          <Skeleton className="h-9 w-40" />
        </div>
        <Skeleton className="h-5 w-28" />
        <Skeleton className="h-24 w-full" />
        <div className="flex justify-end">
          <Skeleton className="h-9 w-44" />
        </div>
      </div>
    </div>
  </div>
);

const InviteStaffScreen: React.FC<{ pageId: string }> = ({ pageId }) => {
  const { page, isLoading } = usePage() as {
    page: { seller?: { _id?: string } };
    isLoading: boolean;
  };
  const { sellerId } = useAuth();

  if (isLoading) return <InviteStaffSkeleton />;

  return <InviteStaffWizard pageId={pageId} sellerId={page?.seller?._id ?? sellerId ?? ''} />;
};

const InviteStaffPage: React.FC = () => {
  const { params } = useRouteMatch<{ id: string }>();
  return (
    <PageProvider pageId={params.id}>
      <InviteStaffScreen pageId={params.id} />
    </PageProvider>
  );
};

export default InviteStaffPage;
